import { IModel, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { ExpressionBasedColumn } from './expression-based.column';
import { format } from 'app/shared/utils/string.util';
import { BasePivotTransformation } from './base-pivottransformation';
import { Dictionary } from 'lodash';
import { ExpressionBasedColumnBase } from './expression-based.column.base';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { DataFlowExpression } from './dataflowexpression';
import { Owned } from '../base/named';
import { Constants } from 'app/shared/shared-constants';
import { DataFlowSchemaAdapterUtils } from '../adapters/dataflow/dataflow-schema-adapter-utils';

export type PivotColumnNamePattern = '{0}$N{1}$V{2}' | '{0}$V{1}$N{2}';

@Metamodel.model({
    name: 'pivot',
    display: [stringResources.DataFlowPivotTransformationLabel, stringResources.DataFlowPivotTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    }
})
export class PivotTransformation extends BasePivotTransformation {
    public static readonly MODEL: IModel;
    public transformationLabel = stringResources.PivotTransformationUILabel;

    @Metamodel.attribute({
        type: DataFlowExpression.MODEL,
        constraint: {
            required: true,
            validator: PivotTransformation.validatePivotKeyColumn
        }
    })
    public pivotKeyColumn: DataFlowExpression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, this);

    @Metamodel.attribute({
        display: stringResources.PivotTransformationExpressionColumn,
        constraint: {
            minOccurs: 1
        }
    })
    public expressionColumns: ExpressionBasedColumnBase[] = [];

    public pivotKeyValues: string[] = [];
    public lateralColumnArrangement: boolean = true;
    public columnNamePattern: PivotColumnNamePattern = '{0}$N{1}$V{2}';
    public prefix: string = '';
    public middle: string = '';
    public suffix: string = '';

    private expressionToPivotColumnMap: Dictionary<Dictionary<ExpressionBasedColumn>> = {};

    public get ColumnNamePatternFormatted(): string {
        return format(this.columnNamePattern, this.prefix, this.middle, this.suffix);
    }

    public get pivotColumns(): ExpressionBasedColumn[] {
        this._pivotColumns = this.generatePivotColumns();
        return this._pivotColumns;
    }

    private static validatePivotKeyColumn(transformation: PivotTransformation, value: DataFlowExpression): IViolation[] | undefined {
        if (!transformation.pivotKeyColumn || !transformation.pivotKeyColumn.expression) {
            return [
                {
                    context: transformation,
                    message: () => format(stringResources.PivotTransformationPivotKeyColumnRequired, transformation.transformationLabel)
                }
            ];
        }
        return undefined;
    }

    public expressionBasedColumnsUpdated(expId: number): void {
        this.generatePivotColumns();
        if (this.expressionToPivotColumnMap[expId]) {
            Object.keys(this.expressionToPivotColumnMap[expId]).forEach((value) => {
                this.columnRenamed(this.expressionToPivotColumnMap[expId][value].column);
            });
        }
    }

    public pivotPatternNameUpdated(): void {
        this.pivotColumns.forEach((pivotCol) => this.columnRenamed(pivotCol.column));
    }

    public generatePivotColumns(): ExpressionBasedColumn[] {
        const existingColumns = (this.expressionColumns.length
            ? this.expressionColumns
            : this.inputColumns.filter((column) => {
                return !this.groupByColumns.find((groupCol) => groupCol.column.name === column.name) && this.pivotKeyColumn.expression !== column.name;
            }).map((column) => ({ column }))) as ExpressionBasedColumn[];

        const values = [].concat(this.pivotKeyValues, this.nullValue ? 'null' : []);

        const expressionColumns: ExpressionBasedColumn[] = [];
        if (this.lateralColumnArrangement) {
            existingColumns.forEach((existingColumn) => {
                values.forEach((keyValue) => {
                    expressionColumns.push(this.getExpressionColumn(keyValue, existingColumn));
                });
            });
        } else {
            values.forEach((keyValue) => {
                existingColumns.forEach((existingColumn) => {
                    expressionColumns.push(this.getExpressionColumn(keyValue, existingColumn));
                });
            });
        }

        return  expressionColumns;
    }

    protected updateInputTransformationCacheInternal(): void {
        super.updateInputTransformationCacheInternal();

        let unescapedExpression = this.pivotKeyColumn.expression;
        if (Constants.dataFlowEscapedNameValidation.test(unescapedExpression)) {
            unescapedExpression = DataFlowSchemaAdapterUtils.removeWrappingCharacters(unescapedExpression);
        }

        const column = this.distinctInputColumnRefs.find((col) => col.name === unescapedExpression);
        if (column) {
            this.pivotKeyColumn.returnType = DataFlowTypesUtils.cloneType(column.column.dataFlowColumnType);
        }
    }

    protected getGeneratedDescription(): string {
        return stringResources.PivotTransformationDescription;
    }

    protected getExpressionColumnsDisplayName(): string {
        return stringResources.PivotTransformationExpressionColumn;
    }

    private getExpressionColumn(value: string, column: ExpressionBasedColumn): ExpressionBasedColumn {
        let expressionColumn: ExpressionBasedColumn;
        if (this.expressionToPivotColumnMap[column.column.id] && this.expressionToPivotColumnMap[column.column.id][value]) {
            expressionColumn = this.expressionToPivotColumnMap[column.column.id][value];
        } else {
            expressionColumn = this.createExpressionBasedColumn();
            this.expressionToPivotColumnMap[column.column.id] = this.expressionToPivotColumnMap[column.column.id] || {};
            this.expressionToPivotColumnMap[column.column.id][value] = expressionColumn;
        }
        expressionColumn.column.name = this.ColumnNamePatternFormatted.replace('$N', column.column.name).replace('$V', value);
        if (column.dataFlowExpression) {
            expressionColumn.dataFlowExpression = column.dataFlowExpression;
        }

        return expressionColumn;
    }
}
